import { Theme, THEMES } from './themes/themes';

const POLL_TIMEOUT = Number.parseInt(process.env.POLL_TIMEOUT || process.env.REACT_APP_POLL_TIMEOUT || '1000');

export const APP_STAGE = {
  Dev: `development`,
  Sit: 'sit', // To be used for SIT environment
  Staging: `staging`, // aka UAT
  Nfr: 'nfr',
  Prod: `production`
} as const;

export type AppStage = (typeof APP_STAGE)[keyof typeof APP_STAGE];

export interface IFrontendConfiguration {
  hostUrl: string;
  apiUrl: string;
  cloudApiUrl: string;
  stockbrokingApiUrl: string;
  stockbrokingUrl: string;
  stockbrokingCloudApiUrl: string;
  marketDataWsUrl: string;
  forgerockBaseUrl: string;
  dojoApiUrl: string;
  proUrl: string;
  signOutTimeoutMins: number;
  appStage: AppStage;
  pollTimeout: number;
}

const baseConfig: IFrontendConfiguration = {
  hostUrl: 'https://hdev.nonprod.cmcmarketsinvest.com',
  apiUrl: `https://api-signup.hdev.nonprod.cmcmarketsinvest.com`,
  cloudApiUrl: `https://dev-api.nonprod.cmcmarketsinvest.com`,
  stockbrokingApiUrl: `https://trading.hdev.nonprod.cmcmarketsinvest.com/ClientRequest/Endpoint?_app.id=CmcAws`,
  stockbrokingCloudApiUrl: `https://dev-api.nonprod.cmcmarketsinvest.com/ClientRequest/Endpoint?_app.id=CmcAws`,
  stockbrokingUrl: `https://trading.hdev.nonprod.cmcmarketsinvest.com`,
  marketDataWsUrl: `wss://ws-marketdata.hdev.nonprod.cmcmarketsinvest.com/ws`,
  forgerockBaseUrl: 'https://idp.dev.nonprod.cmcmarketsinvest.com',
  dojoApiUrl: '/ClientRequest/Endpoint?_app.id=CmcAws',
  proUrl: `https://dev-pro.nonprod.cmcmarketsinvest.com/`,
  signOutTimeoutMins: 60,
  appStage: APP_STAGE.Dev,
  pollTimeout: POLL_TIMEOUT
};

export const devConfig: IFrontendConfiguration = {
  ...baseConfig,
  // Note: Only apply override in dev
  stockbrokingApiUrl: process.env.REACT_APP_STOCKBROKING_DEV_URL || baseConfig.stockbrokingApiUrl
};

export const sitConfig: IFrontendConfiguration = {
  ...baseConfig,
  hostUrl: 'https://hdev3.nonprod.cmcmarketsinvest.com',
  apiUrl: `https://api-signup.hdev3.nonprod.cmcmarketsinvest.com`,
  cloudApiUrl: `https://hdev3-api.nonprod.cmcmarketsinvest.com`,
  stockbrokingApiUrl: `https://trading.hdev3.nonprod.cmcmarketsinvest.com/ClientRequest/Endpoint?_app.id=CmcAws`,
  stockbrokingCloudApiUrl: `https://hdev3-api.nonprod.cmcmarketsinvest.com/ClientRequest/Endpoint?_app.id=CmcAws`,
  stockbrokingUrl: `https://trading.hdev3.nonprod.cmcmarketsinvest.com`,
  marketDataWsUrl: `wss://ws-marketdata.hdev3.nonprod.cmcmarketsinvest.com/ws`,
  forgerockBaseUrl: 'https://idp.hdev3.nonprod.cmcmarketsinvest.com',
  proUrl: `https://hdev3-pro.cmcmarketsstockbroking.com.au/`,
  appStage: APP_STAGE.Sit
};

export const stagingConfig: IFrontendConfiguration = {
  ...baseConfig,
  hostUrl: 'https://uat.nonprod.cmcmarketsinvest.com',
  apiUrl: `https://api-signup.uat.nonprod.cmcmarketsinvest.com`,
  cloudApiUrl: `https://uat-api.nonprod.cmcmarketsinvest.com`,
  stockbrokingApiUrl: `https://trading.uat.nonprod.cmcmarketsinvest.com/ClientRequest/Endpoint?_app.id=CmcAws`,
  stockbrokingCloudApiUrl: `https://uat-api.nonprod.cmcmarketsinvest.com/ClientRequest/Endpoint?_app.id=CmcAws`,
  stockbrokingUrl: `https://trading.uat.nonprod.cmcmarketsinvest.com`,
  marketDataWsUrl: `wss://ws-marketdata.uat.nonprod.cmcmarketsinvest.com/ws`,
  forgerockBaseUrl: 'https://idp.uat.nonprod.cmcmarketsinvest.com',
  proUrl: `https://uat-pro.nonprod.cmcmarketsinvest.com/`,
  appStage: APP_STAGE.Staging
};

export const nfrConfig: IFrontendConfiguration = {
  ...baseConfig,
  hostUrl: 'https://nfr.nonprod.cmcmarketsinvest.com',
  apiUrl: `https://api-signup.nfr.nonprod.cmcmarketsinvest.com`,
  cloudApiUrl: `https://nfr-api.nonprod.cmcmarketsinvest.com`,
  stockbrokingApiUrl: `https://trading.nfr.nonprod.cmcmarketsinvest.com/ClientRequest/Endpoint?_app.id=CmcAws`,
  stockbrokingCloudApiUrl: `https://nfr-api.nonprod.cmcmarketsinvest.com/ClientRequest/Endpoint?_app.id=CmcAws`,
  stockbrokingUrl: `https://trading.nfr.nonprod.cmcmarketsinvest.com`,
  marketDataWsUrl: `wss://ws-marketdata.nfr.nonprod.cmcmarketsinvest.com/ws`,
  forgerockBaseUrl: 'https://idp.nfr.nonprod.cmcmarketsinvest.com',
  proUrl: `https://nfr-pro.nonprod.cmcmarketsinvest.com/`,
  appStage: APP_STAGE.Nfr
};

export const prodConfig: IFrontendConfiguration = {
  ...baseConfig,
  hostUrl: 'https://cmcmarketsinvest.com',
  apiUrl: `https://api-signup.cmcmarketsinvest.com`,
  cloudApiUrl: `https://api.cmcmarketsinvest.com`,
  stockbrokingApiUrl: `https://trading.cmcmarketsinvest.com/ClientRequest/Endpoint?_app.id=CmcAws`,
  stockbrokingCloudApiUrl: `https://api.cmcmarketsinvest.com/ClientRequest/Endpoint?_app.id=CmcAws`,
  stockbrokingUrl: `https://trading.cmcmarketsinvest.com`,
  marketDataWsUrl: `wss://ws-marketdata.cmcmarketsinvest.com/ws`,
  forgerockBaseUrl: 'https://idp.cmcmarketsinvest.com',
  proUrl: `https://pro.cmcmarketsinvest.com/`,
  appStage: APP_STAGE.Prod
};

export const frontendConfig: Record<AppStage, IFrontendConfiguration> = {
  production: prodConfig,
  sit: sitConfig,
  staging: stagingConfig,
  nfr: nfrConfig,
  development: devConfig
};

export const proPlatformThemes: Theme[] = [THEMES.Advantage, THEMES.Bendigo, THEMES.StGeorge, THEMES.CMC, THEMES.CMCAlpha];

export const cfdPlatformThemes: Theme[] = [THEMES.Advantage, THEMES.CMC, THEMES.CMCAlpha, THEMES.CMCSingapore];
